import type { TypedSupabaseClient } from '../client';
import type {
  ISocialRepository,
  SocialAccount,
  SocialPost,
  SocialPostFilters,
  SocialPostResult,
  CreateSocialPostInput,
  UpdateSocialPostInput,
  StartConnectResult,
} from '../../../interfaces/src';
import type {
  SocialMediaItem,
  SocialPostStatus,
  SocialPostFormat,
  SocialPostTarget,
} from '@erp/domain/social';
import { fetchAllRows } from './fetch-all-rows';

// The generated Supabase types are hand-maintained & stale, so reach the new
// tables / RPCs through narrow casts (same idiom as facebook-pixels-repository).
const anyFrom = (client: TypedSupabaseClient) =>
  (table: string) => (client as unknown as { from: (t: string) => any }).from(table);
const anyRpc = (client: TypedSupabaseClient) =>
  (fn: string, args: Record<string, unknown>) =>
    (client as unknown as { rpc: (f: string, a: Record<string, unknown>) => Promise<{ data: any; error: any }> }).rpc(fn, args);

export class SupabaseSocialRepository implements ISocialRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  // ---- Connected accounts ----

  async listAccounts(): Promise<SocialAccount[]> {
    // SECURITY DEFINER RPC: returns non-secret columns only (never the token).
    const { data, error } = await anyRpc(this.supabase)('list_social_accounts', {});
    if (error) throw error;
    return (data ?? []).map(mapAccount);
  }

  async startConnect(returnPath?: string): Promise<StartConnectResult> {
    const { data, error } = await this.supabase.functions.invoke('meta-oauth-start', {
      body: { returnPath: returnPath ?? null },
    });
    if (error) throw error;
    return { url: String((data as { url?: string })?.url ?? '') };
  }

  async disconnectAccount(id: string): Promise<void> {
    // social_accounts has SELECT revoked (the token is secret), and PostgREST
    // needs SELECT to run a DELETE — so a direct .delete() fails. Disconnect
    // goes through a SECURITY DEFINER RPC, tenant-scoped server-side.
    const { error } = await anyRpc(this.supabase)('disconnect_social_account', { p_id: id });
    if (error) throw error;
  }

  // ---- Posts ----

  async listPosts(filters?: SocialPostFilters): Promise<SocialPost[]> {
    const buildQuery = () => {
      let query = anyFrom(this.supabase)('social_posts')
        .select('*')
        .order('created_at', { ascending: false })
        .order('id', { ascending: false });
      if (filters?.status) query = query.eq('status', filters.status);
      return query;
    };

    if (filters?.limit != null) {
      const offset = filters.offset ?? 0;
      const { data, error } = await buildQuery().range(
        offset,
        offset + filters.limit - 1,
      );
      if (error) throw error;
      return (data ?? []).map(mapPost);
    }

    const rows = await fetchAllRows<Record<string, unknown>>((from, to) =>
      buildQuery().range(from, to),
    );
    return rows.map(mapPost);
  }

  async getPost(id: string): Promise<SocialPost | null> {
    const { data, error } = await anyFrom(this.supabase)('social_posts').select('*').eq('id', id).maybeSingle();
    if (error) throw error;
    return data ? mapPost(data) : null;
  }

  async createPost(input: CreateSocialPostInput): Promise<SocialPost> {
    const row = toRow(input);
    row['status'] = input.scheduledAt ? 'scheduled' : 'draft';
    const { data, error } = await anyFrom(this.supabase)('social_posts').insert(row).select().single();
    if (error) throw error;
    return mapPost(data);
  }

  async updatePost(id: string, input: UpdateSocialPostInput): Promise<SocialPost> {
    const { data, error } = await anyFrom(this.supabase)('social_posts')
      .update(toRow(input))
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapPost(data);
  }

  async deletePost(id: string): Promise<void> {
    const { error } = await anyFrom(this.supabase)('social_posts').delete().eq('id', id);
    if (error) throw error;
  }

  async publishNow(id: string): Promise<SocialPost> {
    // The edge function reads the secret token (service role), calls the Meta
    // Graph API, writes results back, and returns the updated post row.
    const { data, error } = await this.supabase.functions.invoke('social-publish', {
      body: { postId: id },
    });
    if (error) throw error;
    const post = (data as { post?: Record<string, unknown> })?.post;
    if (post) return mapPost(post);
    // Fallback: re-fetch if the function returned only a status.
    const refreshed = await this.getPost(id);
    if (!refreshed) throw new Error('social_post_not_found_after_publish');
    return refreshed;
  }
}

// ---- mappers ----

function mapAccount(row: Record<string, unknown>): SocialAccount {
  return {
    id: row['id'] as string,
    tenantId: (row['tenant_id'] as string) ?? '',
    pageId: (row['fb_page_id'] as string) ?? '',
    pageName: (row['fb_page_name'] as string) ?? '',
    igUserId: (row['ig_user_id'] as string) ?? undefined,
    igUsername: (row['ig_username'] as string) ?? undefined,
    avatarUrl: (row['avatar_url'] as string) ?? undefined,
    expiresAt: (row['token_expires_at'] as string) ?? undefined,
    connectedAt: (row['created_at'] as string) ?? '',
    isActive: Boolean(row['is_active']),
  };
}

function mapPost(row: Record<string, unknown>): SocialPost {
  const rawTargets = Array.isArray(row['targets']) ? (row['targets'] as Array<Record<string, unknown>>) : [];
  const rawResults = Array.isArray(row['results']) ? (row['results'] as Array<Record<string, unknown>>) : [];
  return {
    id: row['id'] as string,
    tenantId: (row['tenant_id'] as string) ?? '',
    status: (row['status'] as SocialPostStatus) ?? 'draft',
    format: (row['format'] as SocialPostFormat) ?? 'feed',
    captions: (row['captions'] as Partial<Record<'facebook' | 'instagram', string>>) ?? {},
    media: (Array.isArray(row['media']) ? (row['media'] as SocialMediaItem[]) : []),
    link: (row['link'] as string) ?? undefined,
    targets: rawTargets.map(
      (t): SocialPostTarget => ({
        platform: t['platform'] as SocialPostTarget['platform'],
        accountId: String(t['account_id'] ?? t['accountId'] ?? ''),
      }),
    ),
    scheduledAt: (row['scheduled_at'] as string) ?? null,
    publishedAt: (row['published_at'] as string) ?? null,
    results: rawResults.map(
      (r): SocialPostResult => ({
        platform: r['platform'] as SocialPostResult['platform'],
        accountId: String(r['account_id'] ?? r['accountId'] ?? ''),
        externalId: (r['external_id'] as string) ?? undefined,
        error: (r['error'] as string) ?? undefined,
      }),
    ),
    error: (row['error'] as string) ?? undefined,
    createdAt: (row['created_at'] as string) ?? '',
    updatedAt: (row['updated_at'] as string) ?? '',
  };
}

function toRow(input: UpdateSocialPostInput): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  if (input.captions !== undefined) row['captions'] = input.captions;
  if (input.media !== undefined) row['media'] = input.media;
  if (input.format !== undefined) row['format'] = input.format;
  if (input.link !== undefined) row['link'] = input.link;
  if (input.scheduledAt !== undefined) row['scheduled_at'] = input.scheduledAt;
  if (input.targets !== undefined) {
    row['targets'] = input.targets.map((t) => ({ platform: t.platform, account_id: t.accountId }));
  }
  if ('status' in input && input.status !== undefined) row['status'] = input.status;
  return row;
}
